<script>
   /****************************************************************/
   /* PlotLoadings                                                 */
   /* --------------------                                         */
   /* shows plot with PCA loadings                                 */
   /****************************************************************/

   import { Segments } from 'svelte-plots-basic/2d';
   import { Vector, vector } from 'mdatools/arrays';

   import { AXIS_COLOR } from './Defaults.js';
   import Axes from './Axes.svelte';

   import PlotSeries from './PlotSeries.svelte';
   import { pcamodel_get } from '../wasm/dist/pca.js';


   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   let model = $derived(props ? props.model : null);

   /**
    * Gets vector with values for particular outcome and component from the model object
    * @param {Object} m - model object.
    * @param {Number} a - component index (from 1);
    */
   function getValues(m, a) {
      if (!m) return null;
      if (!a || a < 1 || a > m.ncomp) return null;
      return new Vector(pcamodel_get(m, 'loadings', a))
   }

   // ranges of values to define axis limits
   let xRange = $state();
   let yRange = $state();

   // get values and indices for variables
   let varvalues = $derived(model ? vector(model.varvalues) : null);
   let varindices = $derived(model ? vector(model.varindices) : null);

   // get values for x-component
   let compXValues = $derived(getValues(model, params.comp1));

   // define what to use as values and label for x-axis depending on the plot type
   let x = $derived.by(() => {
      if (!model || !params.comp1) return null;

      if (params.plotType === 'p') {
         return {
            values: compXValues,
            label: `PC${params.comp1}`
         };
      }

      if (params.plotType === 'l' && varvalues) {
         return {
            values: varvalues,
            label: model.varvaluesName + (model.varvaluesUnits ? ', ' + model.varvaluesUnits : '')
         };
      }

      return {
         values: varindices,
         label: 'Variables',
      }
   });

   let xValues = $derived(x ? x.values : null);
   let xLabel = $derived(x ? x.label : null);

   // get values and label for y-component
   let yValues = $derived(getValues(model, params.comp2));
   let yLabel = $derived(`PC${params.comp2}`);


   // reactive expression to compute y-axis limits
   let limYc = $derived.by(() => {
      if (yRange && yRange[0] < Number.POSITIVE_INFINITY && yRange[1] > Number.NEGATIVE_INFINITY) {
         const dY = (yRange[1] - yRange[0]) * 0.10;
         return [yRange[0] - dY, yRange[1] + dY];
      } else {
         return [0, 1];
      }
   });

   // reactive expression to compute x-axis limits
   let limXc = $derived.by(() => {
      if (xRange && xRange[0] < Number.POSITIVE_INFINITY && xRange[1] > Number.NEGATIVE_INFINITY) {
         const dX = (xRange[1] - xRange[0]) * 0.10;
         return params.plotType === 'l' && model.varrev ? [xRange[1] + dX, xRange[0] - dX] : [xRange[0] - dX, xRange[1] + dX];
      } else {
         return [0, 1];
      }
   });

   // adjust limits depending on the axis scale
   let lim = $derived.by(() => {

      if (params.axesEqual) {
         if (params.plotType === 'p') {
            const v = Math.max(Math.abs(limXc[0]), Math.abs(limYc[0]), limXc[1], limYc[1]) * 1.05;
            return {x: [-v, v], y: [-v, v]};
         } else {
            const v = Math.max(Math.abs(limYc[0]), limYc[1]) * 1.05;
            return {x: limXc, y: [-v, v]}
         }
      }

      return { x: limXc, y: limYc };
   });
</script>

{#if xValues && yValues }
<Axes title='Loadings' fileName='plot-loadings' {xLabel} {yLabel} limX={lim.x} limY={lim.y}>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[lim.x[0]]} xEnd={[lim.x[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   {#if params.plotType === 'p'}
   <Segments xStart={[0]} xEnd={[0]} yStart={[lim.y[0]]} yEnd={[lim.y[1]]} lineColor={AXIS_COLOR} />
   {/if}

   <!-- data points  -->
   <PlotSeries
      {xValues}
      {yValues}
      {selected}
      seriesTitle="loadings"
      plotType={params.plotType}
      showLabels={params.showLabels}
      labels={model.varlabels}
      shortenLabels={false}
      bind:xRange={xRange}
      bind:yRange={yRange}
   />
</Axes>
{/if}
